import { currentSchemaVersion } from '$domain/events';
import type { DomainEvent, EventType, PayloadOf, TypedEvent } from '$domain/events';
import type { Clock } from '$infra/clock';
import { monotonicFactory } from 'ulid';

export interface EventFactoryDependencies {
  clock: Clock;
  deviceId: string;
  /**
   * The timestamp of the newest event already in the log, in ms.
   *
   * **Why this is not optional in practice.** The store returns events in ULID
   * order and reducers read that order as chronological, so an id must never
   * sort before an id already written. `monotonicFactory()` guarantees that
   * within a session — but its state lives in module memory and dies with the
   * page. A device whose clock is corrected backwards while the app is closed
   * (NTP catching up on a phone that booted with a wrong RTC, a manual timezone
   * fix, a DST-confused embedded clock) would mint a *smaller* ULID on the next
   * launch. The new event would then replay before events it followed in real
   * life, and a sync outbox reading `getSince(watermark)` would never see it at
   * all, because it sorts behind the watermark.
   *
   * Seeding the floor from the log closes that: time may go backwards, ids may
   * not.
   */
  floorMs?: number;
}

export interface EventFactory {
  makeEvent<T extends EventType>(type: T, payload: PayloadOf<T>): TypedEvent<T>;
  /**
   * Raise the floor after events arrive from elsewhere.
   *
   * A sync download can union in events newer than anything this device has
   * written (I6). Without this, the next locally minted id could sort before
   * them and land in the middle of the log.
   */
  raiseFloor(ms: number): void;
}

export function createEventFactory({
  clock,
  deviceId,
  floorMs = 0,
}: EventFactoryDependencies): EventFactory {
  // Per factory, not per module: two factories in one process (tests, and the
  // two-client sync harness in I6) must not share monotonic state.
  const nextUlid = monotonicFactory();
  let floor = floorMs;

  return {
    makeEvent<T extends EventType>(type: T, payload: PayloadOf<T>): TypedEvent<T> {
      const wallClock = clock.now().getTime();

      // `floor + 1`, not `floor`. Minting at exactly the floor produces a ULID
      // with the *same* timestamp component as the newest stored event, and two
      // ULIDs sharing a timestamp are ordered only by their random bits — so
      // "greater than everything already written" would be a coin flip. One
      // millisecond past the floor makes it an arithmetic fact.
      //
      // The floor is deliberately not advanced per event: once the wall clock
      // overtakes it, real time takes over again and `monotonicFactory` handles
      // same-millisecond ordering within the session. Bumping it every call
      // would drift the log's ids steadily ahead of the actual time.
      const stamp = wallClock > floor ? wallClock : floor + 1;

      const event: DomainEvent<PayloadOf<T>> & { type: T } = {
        id: nextUlid(stamp),
        type,
        // The *timestamp* stays honest even when the id's does not. If the
        // device clock is wrong, `occurredAt` records what the device believed;
        // inventing a corrected time would be a worse lie than an ordering
        // artefact, and insights are about the user's own sense of when things
        // happened.
        occurredAt: clock.nowIso(),
        deviceId,
        schemaVersion: currentSchemaVersion(type),
        payload,
      };
      return event;
    },

    raiseFloor(ms: number): void {
      if (ms > floor) floor = ms;
    },
  };
}
